import { Box, Button, Typography, Hidden, IconButton, Tooltip, Switch, Stack } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import { useDispatch } from 'react-redux';
import {
  RtaApproverAccess,
  getRTAApproverList,
  getRtaApproverAccess,
  UpdateRtaApprover,
} from '../../redux-store/actions/userManagement';
import { AddRtaApproverRequestBody, RtaApprover } from '../../redux-store/types/api-types';
import { ColumnType, DataTable } from '../DataTable';
import { useHistory } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import { ConfirmationDialog } from '../commonComponents';
import { AccessHeader } from './relationShipManagers';

const columns: ColumnType[] = [
  {
    header: 'Name',
    renderCell: (row: RtaApprover) => {
      return (
        <Typography
          variant="h5"
          sx={{
            color: '#61D1D6',
            textDecoration: 'none',
            fontWeight: 500,
            fontSize: '14px',
            ':hover': {
              color: 'primary.main',
            },
          }}
          component={RouterLink}
          to={{ pathname: 'amc-checker-details', state: { rtaApproverId: row.id } }}>
          {row.user.firstName || 'N/A'}
        </Typography>
      );
    },
  },
  {
    header: 'Mobile Number',
    valueGetter: (row: RtaApprover) =>
      row.user?.countryCode
        ? row.user?.countryCode + '-' + row.user?.phone
        : row.user.phone || 'N/A',
  },
  {
    header: 'Email ID',
    valueGetter: (row: RtaApprover) => row.user.email || 'N/A',
  },
];

function rtaApprover(): JSX.Element {
  const dispatch = useDispatch();
  const history = useHistory();
  const [isLoading, setLoading] = useState(false);
  const [rtaApproverList, setRtaApproverList] = useState<RtaApprover[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [openDialog, setDialog] = useState<{
    rtaApproverRowData: AddRtaApproverRequestBody;
  } | null>(null);
  const [accessForEditAdd, setAccessForEditAdd] = useState(false);

  useEffect(() => {
    let isComponentAlive = true;
    (async function () {
      try {
        setLoading(true);
        const res = (await dispatch(getRTAApproverList())) as unknown as RtaApprover[];
        const { rtaApproverAccess } = (await dispatch(
          getRtaApproverAccess()
        )) as unknown as RtaApproverAccess;
        if (!isComponentAlive) return;
        setRtaApproverList(res);
        setAccessForEditAdd(rtaApproverAccess);
        setLoading(false);
      } catch (e) {
        console.error((e as Error).message);
      } finally {
        if (isComponentAlive) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isComponentAlive = false;
    };
  }, []);

  const handlePageChange = (event: ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const AccessViewOrEditCell = [
    {
      header: AccessHeader,
      renderCell: (row: AddRtaApproverRequestBody): JSX.Element => {
        return (
          <>
            <Stack
              sx={{
                alignItems: { xs: 'baseline', sm: 'center' },
                '.MuiSwitch-root': {
                  paddingLeft: { xs: '0px', sm: '12px' },
                  width: { xs: '52px', sm: '58px' },
                },
                '.MuiSwitch-switchBase': { paddingLeft: { xs: '0px', sm: '9px' } },
              }}>
              <Switch
                sx={{ '.MuiSwitch-switchBase': { color: 'black' } }}
                checked={row.isActive}
                onChange={() => setDialog({ rtaApproverRowData: row })}
                disabled={!accessForEditAdd}
              />
            </Stack>
          </>
        );
      },
    },
    // {
    //   header: '',
    //   renderCell: (row: RtaApprover): JSX.Element => {
    //     return (
    //       <>
    //         {accessForEditAdd && row.isActive ? (
    //           <Tooltip title="Edit">
    //             <IconButton
    //               component={RouterLink}
    //               to={{ pathname: 'edit-rtaapprover', state: { rtaApproverId: row.id } }}
    //               sx={{
    //                 ':hover': {
    //                   bgcolor: '#F4FCFC',
    //                 },
    //                 '&.Mui-disabled': {
    //                   cursor: 'pointer',
    //                   pointerEvents: 'all',
    //                 },
    //               }}>
    //               <EditIcon />
    //             </IconButton>
    //           </Tooltip>
    //         ) : (
    //           <Typography
    //             variant="h5"
    //             component={RouterLink}
    //             to={{ pathname: 'edit-rtaapprover', state: { rtaApproverId: row.id } }}
    //             sx={{
    //               color: '#61D1D6',
    //               textDecoration: 'none',
    //               fontWeight: 500,
    //               fontSize: '14px',
    //               ':hover': {
    //                 color: 'primary.main',
    //               },
    //             }}>
    //             View Details
    //           </Typography>
    //         )}
    //       </>
    //     );
    //   },
    // },
  ];

  return (
    <>
      <Box sx={{ bgcolor: 'common.white', px: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            pt: { xs: 2, sm: 7 },
            pb: { xs: 2, sm: 4 },
          }}>
          <Typography sx={{ fontSize: { xs: 15, sm: 25 }, fontWeight: 500 }}>
            List of AMC Checkers
          </Typography>
          {accessForEditAdd && (
            <Hidden only="xs">
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                sx={{
                  color: 'white',
                  fontWeight: 500,
                  fontSize: 14,
                  mr: 2.5,
                  py: 0.5,
                  px: 3,
                }}
                onClick={() => history.push('add-amc-checker')}>
                Add AMC Checker
              </Button>
            </Hidden>
          )}
          {accessForEditAdd && (
            <Hidden smUp={true}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                sx={{
                  color: 'primary.main',
                  fontWeight: 500,
                  fontSize: 13,
                  // mr: 2.5,
                  py: 0.5,
                  // px: 3,
                  // whiteSpace: 'nowrap',
                }}
                onClick={() => history.push('add-amc-checker')}>
                Add AMC Checker
              </Button>
            </Hidden>
          )}
        </Box>
        {useMemo(
          () => (
            <DataTable
              tableData={rtaApproverList}
              tableHeader={[...columns, ...AccessViewOrEditCell]}
              currentPage={currentPage}
              isLoading={isLoading}
              noOfPages={1}
              paginationAction={handlePageChange}
              renderAdditionalRow={true}
              tableBodyCustomStyles={{
                '.MuiTableCell-root': {
                  borderBottom: 'none',
                },
              }}
              tableHeaderCustomStyles={{
                '&:last-child th': { border: 0 },
                '.MuiTableCell-head': {
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: 14,
                  fontWeight: 500,
                  color: 'primary.main',
                },
              }}
            />
          ),
          [rtaApproverList, currentPage, isLoading]
        )}
      </Box>
      <ConfirmationDialog
        message={`Are you sure you want to ${
          openDialog?.rtaApproverRowData.isActive ? 'disable' : 'enable'
        } the access to this user ?`}
        open={openDialog !== null}
        setOpen={() => setDialog(null)}
        onSave={async () => {
          setDialog(null);
          try {
            if (openDialog) {
              const updatedResponse = (await dispatch(
                UpdateRtaApprover(openDialog.rtaApproverRowData.id as number, {
                  ...openDialog.rtaApproverRowData,
                  isActive: !openDialog?.rtaApproverRowData.isActive,
                })
              )) as unknown as RtaApprover;
              setRtaApproverList(
                rtaApproverList?.map((rtaApprover) => {
                  if (Number(rtaApprover.id) === Number(updatedResponse.id)) {
                    return {
                      ...rtaApprover,
                      ...updatedResponse,
                    };
                  }
                  return rtaApprover;
                })
              );
            }
          } catch (e) {
            console.error((e as Error).message);
          }
        }}
        onCancel={() => setDialog(null)}
      />
    </>
  );
}

export default rtaApprover;
